"use client";

import { ArrowRight, ClipboardList, FileText, MapPin, Truck, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Process() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Bezpłatna wycena",
    description: "Prześlij nam projekt lub umów się na wizję lokalną, aby otrzymać dokładny kosztorys inwestycji.",
    Icon: FileText
  }, {
    number: "02",
    title: "Planowanie prac",
    description: "Przygotowujemy harmonogram robót, dobieramy materiały i ustalamy logistykę budowy.",
    Icon: ClipboardList
  }, {
    number: "03",
    title: "Realizacja budowy",
    description: "Nasz zespół przystępuje do prac ziemnych, fundamentowych lub wykończeniowych zgodnie z planem.",
    Icon: Truck
  }, {
    number: "04",
    title: "Odbiór inwestycji",
    description: "Finalizujemy prace, sprzątamy teren i przekazujemy gotowy obiekt do użytkowania.",
    Icon: PackageOpen
  }];
  return <section data-section-id="688" id="jak-wygląda-proces" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse 60% 50% at 50% 50%, hsla(205, 95%, 50%, 0.12), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="font-default inline-block text-sm font-semibold tracking-[0.2em] uppercase text-primary">
            Jak działamy
          </span>
          <h2 className="font-default mt-4 text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.05] tracking-tight text-foreground">
            Twój projekt w dobrych rękach.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Cztery przejrzyste etapy od pierwszego kontaktu do finalnego odbioru. Profesjonalizm, terminowość i pełna kontrola nad budową.
          </p>
        </div>

        {}
        <div ref={timelineRef} className="relative mt-20 md:mt-24">
          {}
          <div aria-hidden="true" className="hidden md:block absolute left-0 right-0 top-8 h-px" style={{
          top: "2rem"
        }}>
            <div className="relative mx-16 h-px">
              <div className="absolute inset-0 border-t-2 border-dashed border-border" />
              <div className={`absolute inset-y-0 left-0 border-t-2 border-dashed border-primary transition-all duration-[2000ms] ease-out`} style={{
              width: timelineVisible ? "100%" : "0%"
            }} />
              {}
              <div className={`absolute -top-3 text-primary transition-all duration-[2000ms] ease-out`} style={{
              left: timelineVisible ? "calc(100% - 24px)" : "0%"
            }}>
                <Truck className="h-6 w-6" />
              </div>
            </div>
          </div>

          {}
          <div aria-hidden="true" className="md:hidden absolute left-8 top-0 bottom-0 w-px">
            <div className="relative h-full w-px">
              <div className="absolute inset-0 border-l-2 border-dashed border-border" />
              <div className="absolute inset-x-0 top-0 border-l-2 border-dashed border-primary transition-all duration-[2000ms] ease-out" style={{
              height: timelineVisible ? "100%" : "0%"
            }} />
            </div>
          </div>

          {}
          <div aria-hidden="true" className={`hidden md:flex absolute left-1/2 -translate-x-1/2 -top-6 flex-col items-center transition-all duration-700 delay-700 ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}>
            <div className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-semibold tracking-wider uppercase text-primary-foreground shadow-md">
              <MapPin className="h-3.5 w-3.5" />
              BudSys Śląsk
            </div>
            <div className="h-3 w-px bg-primary" />
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
            {steps.map((step, i) => {
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} className={`relative md:text-center pl-20 md:pl-0 transition-all duration-700 ease-out ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: `${200 + i * 150}ms`
            }}>
                  {}
                  <div className="absolute md:relative left-0 md:left-auto top-0 md:mx-auto md:mb-6 flex h-16 w-16 items-center justify-center rounded-lg border-2 border-primary bg-background shadow-lg">
                    <span className="font-default text-2xl font-bold text-primary tabular-nums">
                      {step.number}
                    </span>
                  </div>

                  {}
                  <div className="mt-6 md:mt-0 mb-4 md:flex md:justify-center">
                    <Icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  </div>

                  {}
                  <h3 className="font-default text-xl md:text-[1.375rem] font-semibold text-foreground">
                    {step.title}
                  </h3>

                  {}
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground md:max-w-[14rem] md:mx-auto">
                    {step.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className={`mt-20 md:mt-24 flex justify-center transition-all duration-700 delay-500 ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="ghost" size="lg" className="text-primary hover:text-primary hover:bg-primary/10 font-semibold text-base group">
            <Link to="/PricingPage">
              Zamów bezpłatną wycenę
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}